/*
 * BillingSettingsPage — tenant admin sees current tier + upgrades via Stripe.
 *
 * Route: /portal/settings/billing
 * Supports:
 *   - Displaying the plans (fetched from /api/billing/plans)
 *   - Upgrade → creates Stripe Checkout session → redirects to Stripe
 *   - Success return (?session_id=…) → polls status → shows confirmation
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from './api';
import { toast } from '../hooks/use-toast';
import { errorMessage } from './errors';
import { Check, Sparkles, CreditCard, Loader2, ExternalLink } from 'lucide-react';

// Where "Contact sales" goes. Unset → the button is hidden, never a literal address.
const CONTACT_EMAIL = (process.env.REACT_APP_CONTACT_EMAIL || '').trim();

export default function BillingSettingsPage({ contactEmail = CONTACT_EMAIL }) {
  const [params] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [currentTier, setCurrentTier] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [status, setStatus] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [retrying, setRetrying] = useState(false);

  const load = () => api.get('/billing/plans').then(({ data }) => {
    setPlans(data.plans);
    setCurrentTier(data.current_tier);
    setLoadError('');
  }).catch((e) => {
    setLoadError(errorMessage(e, 'Could not load the plans.'));
  });

  const retry = () => {
    setRetrying(true);
    load().finally(() => setRetrying(false));
  };

  useEffect(() => { load(); }, []);

  // Success polling — after Stripe redirect
  useEffect(() => {
    const sid = params.get('session_id');
    if (!sid) return;
    setConfirming(true);
    let tries = 0;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        setStatus(data);
        if (data.payment_status === 'paid') {
          clearInterval(t);
          setConfirming(false);
          toast({ title: 'Payment confirmed', description: 'Your tenant has been upgraded.' });
          load();
        }
      } catch (e) { /* keep polling */ }
      tries += 1;
      if (tries > 15) { clearInterval(t); setConfirming(false); }
    }, 2000);
    return () => clearInterval(t);
  }, [params]);

  const upgrade = async (lookup_key) => {
    if (!lookup_key) {
      if (contactEmail) window.location.href = `mailto:${contactEmail}?subject=Enterprise%20Plan%20Inquiry`;
      return;
    }
    setBusy(lookup_key);
    try {
      const { data } = await api.post('/payments/checkout', {
        lookup_key,
        quantity: 1,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast({ title: 'Checkout failed', description: e.response?.data?.detail || 'Try again' });
      setBusy(null);
    }
  };

  const canceled = params.get('canceled') === '1';

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="billing-settings-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: '#6BA82E' }}>Billing</div>
        <h1 className="font-serif text-3xl font-bold" style={{ color: '#070B5F' }}>Subscription & upgrades</h1>
        <div className="text-sm text-slate-500 mt-1">
          Current tier: <span className="font-semibold" style={{ color: '#070B5F' }} data-testid="current-tier">
            {currentTier ? currentTier.charAt(0).toUpperCase() + currentTier.slice(1) : '—'}
          </span>
        </div>
      </div>

      {confirming && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 flex items-center gap-3" data-testid="confirming-payment">
          <Loader2 size={16} className="animate-spin text-blue-700" />
          <div className="text-sm font-semibold text-blue-900">Confirming your payment with Stripe…</div>
        </div>
      )}
      {status?.payment_status === 'paid' && (
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 flex items-center gap-3" data-testid="payment-success">
          <Check size={16} className="text-emerald-700" />
          <div className="text-sm font-semibold text-emerald-900">Payment confirmed. Your tenant has been upgraded.</div>
        </div>
      )}
      {canceled && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="payment-canceled">
          Payment canceled. No changes made.
        </div>
      )}

      {loadError && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-center justify-between gap-3" role="alert" data-testid="billing-load-error">
          <div className="text-sm text-red-900">{loadError}</div>
          <button
            onClick={retry}
            disabled={retrying}
            data-testid="billing-load-retry"
            className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-white text-red-900 border border-red-200 hover:bg-red-100 disabled:opacity-50"
          >Retry</button>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((p) => {
          const isCurrent = currentTier === p.key || (p.key === 'pro_yearly' && currentTier === 'pro');
          return (
            <div key={p.key} className={`p-6 rounded-2xl border-2 relative ${p.highlight ? 'border-[#6BA82E]' : 'border-slate-200'}`}
                 data-testid={`plan-${p.key}`}
                 style={{ background: p.highlight ? 'linear-gradient(180deg, #F0FDF4, #ffffff)' : '#ffffff' }}>
              {p.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest text-white"
                     style={{ background: '#6BA82E' }}>Recommended</div>
              )}
              <div className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: '#070B5F' }}>{p.name}</div>
              <div className="flex items-baseline gap-1 mb-2">
                {p.contact_sales
                  ? <span className="text-2xl font-bold text-slate-800">Custom</span>
                  : <>
                      <span className="text-3xl font-bold" style={{ color: '#070B5F' }}>${p.price}</span>
                      <span className="text-xs text-slate-500">/{p.period}</span>
                    </>}
              </div>
              <ul className="space-y-2 mb-6">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs" style={{ color: '#334155' }}>
                    <Check size={12} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {(!p.contact_sales || contactEmail || isCurrent) && (
              <button
                onClick={() => upgrade(p.lookup_key)}
                disabled={(busy != null && busy === p.lookup_key) || isCurrent}
                data-testid={`upgrade-${p.key}`}
                className="w-full py-2.5 rounded-lg text-sm font-semibold transition inline-flex items-center justify-center gap-2 disabled:opacity-50"
                style={isCurrent
                  ? { background: '#f1f5f9', color: '#64748b' }
                  : p.highlight
                    ? { background: '#070B5F', color: '#fff' }
                    : { background: '#fff', color: '#070B5F', border: '1px solid #e2e8f0' }}
              >
                {busy != null && busy === p.lookup_key ? <Loader2 size={13} className="animate-spin" />
                  : isCurrent ? 'Current plan'
                  : p.contact_sales ? <>Contact sales <ExternalLink size={12} /></>
                  : <><CreditCard size={13} /> Upgrade</>}
              </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="text-xs text-slate-500 text-center pt-4">
        Powered by <span className="font-semibold">Stripe</span>. Secure payment. Cancel anytime from this page.
      </div>
    </div>
  );
}
